import React, { useEffect, useState } from "react";
import IntroStep from "./Survey/IntroStep";
import ProgressDashes from "./Survey/ProgressDashes";
import QuestionStep from "./Survey/QuestionStep";
import SurveyFrame from "./Survey/SurveyFrame";
import ThanksStep from "./Survey/ThanksStep";
import { SurveyProvider } from "./Survey/SurveyContext";
import useDismissal from "./Survey/useDismissal";
import useSaveResponse from "./Survey/useSaveResponse";
import useSurveyFlow from "./Survey/useSurveyFlow";
import { trackAmplitudeEvent } from "../amplitude";

export default function Survey({
  createUrl,
  hideWhileOpen,
  identifier,
  intro,
  otherLabel,
  otherTextMax,
  placement,
  prefill,
  preview,
  questions,
  thanks,
  userUuid
}) {
  const flow = useSurveyFlow({ intro, prefill, questions });
  const saver = useSaveResponse({ createUrl, identifier, preview, userUuid });
  const { closed, close } = useDismissal({ identifier, remember: !preview && placement !== "page" });
  const [fadingOut, setFadingOut] = useState(false);
  const onThanks = flow.stepIndex === flow.thanksIndex;

  useEffect(() => {
    if(closed) {
      return;
    }
    if(!preview) {
      trackAmplitudeEvent("web_app_survey_shown", { placement, survey: identifier });
    }
    // Saved here, not on the email link, so mail scanners following links can't record answers.
    flow.prefilled.forEach((question) => (
      saver.save(question, flow.responses[question.identifier]).fail(() => saver.setFailed(true))
    ));
  }, []);

  useEffect(() => {
    saver.setFailed(false);
    if(!onThanks || placement === "page") {
      return undefined;
    }
    const fade = setTimeout(() => setFadingOut(true), 1000);
    const timeout = setTimeout(() => close({ finished: true }), 1000 + (thanks.fadeOutDelaySeconds * 1000));
    return () => {
      clearTimeout(fade);
      clearTimeout(timeout);
    };
  }, [flow.stepIndex]);

  // The pro page hides its content while the post-signup survey is open.
  useEffect(() => {
    const hidden = hideWhileOpen && document.querySelector(hideWhileOpen);
    if(!hidden) {
      return undefined;
    }
    // The server hides it too, so a survey dismissed earlier in this browser must reveal it.
    if(closed) {
      $(hidden).show();
      return undefined;
    }
    hidden.style.display = "none";
    return () => $(hidden).fadeIn();
  }, [closed]);

  if(closed) {
    return null;
  }

  const submit = (response) => {
    const from = flow.stepIndex;
    flow.updateResponse(response);
    saver.submit(flow.question, response, () => flow.advanceFrom(from));
  };

  return (
    <SurveyFrame
      closable={preview || placement !== "page"}
      fadingOut={fadingOut}
      onClose={() => close({ finished: onThanks })}
      placement={placement}
      step={flow.stepIndex}
    >
      {flow.step.type === "intro" && <IntroStep intro={intro} onContinue={flow.next} />}
      {flow.question && (
        <SurveyProvider value={{ flow, otherLabel, otherTextMax, saver, submit }}>
          <QuestionStep />
        </SurveyProvider>
      )}
      {flow.step.type === "thanks" && <ThanksStep thanks={thanks} />}
      <ProgressDashes current={flow.stepIndex} total={flow.thanksIndex} />
    </SurveyFrame>
  );
}
